import _ from 'underscore';
import { EditorState } from 'draft-js';
import { Map } from 'immutable';
import draftToHtml from 'draftjs-to-html';
import { ColorRGBs } from 'flux/stores/cuesheet/CuesheetConstants';
import {
  isCompact,
  isFeatured,
  isGlobalElementManager,
} from './cuesheetViewHelper';
import classNames from 'classnames';
import { imageHeightCache } from 'shared/flux/helpers/preloadItemSetImages';

/*************************
 * Start Debugging
 *
 * Step 1. Change debug variable to true.
 * Step 2. Add "Debug-Cell-Height" text into the cell whose height you're
 * trying to debug.
 * Step 3. Uncomment debug function inside of the getHeight function
 */

// eslint-disable-next-line no-unused-vars
const debug = false;
const cellElementMock = _getCellElementMock();
const cuesheetElementMock = document.createElement('div');
_setCuesheetElementMockClasses();
cuesheetElementMock.appendChild(cellElementMock);

const heightHelperContainer = document.createElement('div');
heightHelperContainer.id = 'heightHelperContainer';
heightHelperContainer.style.position = 'fixed';
heightHelperContainer.style.visibility = 'hidden';
heightHelperContainer.style.left = '-3000px';
heightHelperContainer.appendChild(cuesheetElementMock);

document.body.appendChild(heightHelperContainer);

/**
 * Row Height Helper
 *
 * A simple utility that measures the height of rendered text
 *
 */
export default {
  init() {
    _setCuesheetElementMockClasses();
  },

  /**
   * Get the height of an element
   *
   *
   */
  getHeight(width, rteState, styles, className) {
    const jsRteState = Map.isMap(rteState) ? rteState.toJS() : rteState;

    _renderRTEHeightElementHelper(jsRteState, className, width, styles);
    /*
    if (debug && jsRteState && jsRteState.blocks) {
      const debugCell = _findTextInRteState(jsRteState, 'Debug-Cell-Height');
      if (debugCell) {
        let debugNode = heightHelperContainer.cloneNode(true);
        debugNode.style.left = '0px';
        debugNode.style.visibility = null;
        document.body.appendChild(debugNode);
      }
    }
    */
    return _getCuesheetElementMockHeight();
  },

  getNonRTEHeight(width, markup) {
    const newCellElementMock = _getCellElementMock(width);

    newCellElementMock.innerHTML = `<div class="height-helper-inner">${markup}</div>`;

    cuesheetElementMock.childNodes[0].replaceWith(newCellElementMock);

    return _getCuesheetElementMockHeight();
  },
};

function _getCuesheetElementMockHeight() {
  return Math.max(
    cuesheetElementMock.childNodes[0].scrollHeight,
    cuesheetElementMock.childNodes[0].offsetHeight,
  );
}

function _getCellElementMock(width = 0, className = '', styles = []) {
  const cellElementMock = document.createElement('div');
  cellElementMock.style.width = `${width}px`;
  cellElementMock.className = className;
  _.each(styles, (v, k) => (cellElementMock.style[k] = v));
  cellElementMock.id = 'heightHelper';
  return cellElementMock;
}

function _setCuesheetElementMockClasses() {
  const isCompactView = isCompact();
  const isFeaturedView = isFeatured();
  const isGlobalElementView = isGlobalElementManager();

  const parentClass = classNames('cuesheet', {
    'cuesheet-compact': isCompactView,
    'cuesheet-compact cuesheet-global': isGlobalElementView,
    'cuesheet-featured cuesheet-compact': isFeaturedView,
  });

  if (cuesheetElementMock.className === parentClass) return;

  cuesheetElementMock.className = parentClass;
}

/**
 * Create RTE Height Helper Element
 */
function _renderRTEHeightElementHelper(rteState, className, width, styles) {
  /**
   * Apply classes and styles to container element
   */
  const newCellElementMock = _getCellElementMock(width, className, styles);
  rteState = rteState || EditorState.createEmpty().getCurrentContent().toJS();

  let srcToKeyMap = {};

  _.each(rteState.entityMap, ({ data, type }) => {
    if (type === 'IMAGE') {
      const imgSrc = data.signedSrc || data.src;
      const imgKey = data.s3Key || imgSrc;
      srcToKeyMap[imgSrc] = imgKey;
    }
  });

  const markup = draftToHtml(rteState, false, false, false, ColorRGBs);

  newCellElementMock.innerHTML =
    '<div class="height-helper-inner">' + markup + '</div>';

  const images = newCellElementMock.getElementsByTagName('img');

  for (let i = 0; i < images.length; i++) {
    let img = images[i];
    let key = srcToKeyMap[img.src];
    if (!img.width && imageHeightCache[key]?.naturalWidth)
      img.width = imageHeightCache[key].naturalWidth;
    if (!img.height && imageHeightCache[key]?.naturalHeight)
      img.height = imageHeightCache[key].naturalHeight;
  }

  cuesheetElementMock.childNodes[0].replaceWith(newCellElementMock);
}

// eslint-disable-next-line no-unused-vars
function _findTextInRteState(rteState, text) {
  if (!rteState || !rteState.blocks) return false;

  return rteState.blocks.find((block) => {
    return block.text && block.text.indexOf(text) !== -1;
  });
}
